import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthResult, PlayerProfile } from '@aiscoreboard/contracts';
import { fetchMe, logout, storeToken, storedToken, type Seat } from './accounts';
import {
  SEAT_TOKEN_KEYS, SESSION_TIMEOUT_MS, sessionDeadline, validSessionToken, watchSessionExpiry,
} from './sessionTimeout';

export interface SeatSession {
  token: string;
  player: PlayerProfile;
}

/**
 * What became of a sign-in attempt.
 *
 * `taken` names the seat the account is *already* in, which is the only thing
 * the refusal message has to say to be useful.
 */
export type SignInOutcome = { ok: true } | { ok: false; taken: Seat };

/** Idle expiry is only for seats outside a match and without a post-match deadline. */
const IDLE_TICK_MS = 15 * 1000;

/**
 * Holds the signed-in player for each seat.
 *
 * Seats are independent so two accounts can be on the same tablet, and each
 * token is revalidated on load — an expired or closed account must not leave
 * a ghost name on the scoreboard.
 */
export function useSeats(matchOpen = false) {
  const [seats, setSeats] = useState<[SeatSession | null, SeatSession | null]>([null, null]);
  const [ready, setReady] = useState(false);
  const lastActivity = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;

    const restore = async (seat: Seat): Promise<SeatSession | null> => {
      const token = storedToken(seat);
      if (!token) return null;
      try {
        const player = await fetchMe(token);
        return validSessionToken(SEAT_TOKEN_KEYS[seat]) === token ? { token, player } : null;
      } catch (error) {
        console.warn('[seats] Could not restore player session', error);
        if (storedToken(seat) === token) storeToken(seat, null);
        return null;
      }
    };

    void Promise.all([restore(0), restore(1)]).then(([a, b]) => {
      if (cancelled) return;
      // The same rule as `signIn`, applied to what was left in storage: two
      // tabs, or a sign-in that raced a reload, could otherwise bring the same
      // account back into both seats.
      let second = b;
      if (a && second && a.player.id === second.player.id) {
        void logout(second.token).catch(() => undefined);
        storeToken(1, null);
        second = null;
      }
      setSeats([a, second]);
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const seatsRef = useRef(seats);
  useEffect(() => {
    seatsRef.current = seats;
  }, [seats]);

  /**
   * Put an account in a seat, unless it is already sitting in the other one.
   *
   * One person cannot play both sides of a frame, so an account in two seats
   * is always a mistake — usually somebody signing in again because they did
   * not notice they were already on the board. Letting it happen would credit
   * both halves of the result to the same player. The freshly minted session
   * is revoked on the way out: it was never used, and leaving it alive would
   * hand out a token nobody is holding.
   */
  const signIn = useCallback((seat: Seat, auth: AuthResult): SignInOutcome => {
    const other: Seat = seat === 0 ? 1 : 0;
    const rival = seatsRef.current[other];
    if (rival && rival.player.id === auth.player.id) {
      void logout(auth.token).catch(() => undefined);
      return { ok: false, taken: other };
    }

    storeToken(seat, auth.token);
    lastActivity.current = Date.now();
    setSeats((prev) => {
      const next: [SeatSession | null, SeatSession | null] = [...prev];
      next[seat] = { token: auth.token, player: auth.player };
      return next;
    });
    return { ok: true };
  }, []);

  const signOut = useCallback((seat: Seat): void => {
    const token = storedToken(seat);
    storeToken(seat, null);
    if (token) void logout(token).catch(error => console.warn('[seats] Could not revoke player session', error));
    setSeats(prev => {
      const next: [SeatSession | null, SeatSession | null] = [...prev];
      next[seat] = null;
      return next;
    });
  }, []);

  const update = useCallback((seat: Seat, player: PlayerProfile): void => {
    setSeats((prev) => {
      const session = prev[seat];
      if (!session) return prev;
      const next: [SeatSession | null, SeatSession | null] = [...prev];
      next[seat] = { ...session, player };
      return next;
    });
  }, []);

  const keepAwake = useCallback((): void => {
    lastActivity.current = Date.now();
  }, []);

  const signedIn = seats[0] !== null || seats[1] !== null;

  useEffect(() => watchSessionExpiry(() => {
    let nearest: number | null = null;
    for (const seat of [0, 1] as const) {
      const key = SEAT_TOKEN_KEYS[seat];
      const token = storedToken(seat);
      const deadline = token ? sessionDeadline(key, token) : null;
      if (deadline !== null && deadline <= Date.now()) signOut(seat);
      else if (deadline !== null) nearest = nearest === null ? deadline : Math.min(nearest, deadline);
      const current = storedToken(seat);
      setSeats(prev => {
        if (!prev[seat] || prev[seat]?.token === current) return prev;
        const next: [SeatSession | null, SeatSession | null] = [...prev];
        next[seat] = null;
        return next;
      });
    }
    return nearest;
  }), [signOut]);

  useEffect(() => {
    if (!signedIn || matchOpen) return undefined;
    // Pointer *down* and key *down* rather than movement: a cat on a desk, a
    // trackpad nudged by a sleeve or a cursor drifting over a screensaver are
    // not somebody using the board, and a kiosk that never signs out is the
    // failure this exists to prevent.
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
    for (const name of events) window.addEventListener(name, keepAwake, { passive: true });

    const tick = window.setInterval(() => {
      if (Date.now() - lastActivity.current < SESSION_TIMEOUT_MS) return;
      for (const seat of [0, 1] as const) {
        const token = storedToken(seat);
        if (token && sessionDeadline(SEAT_TOKEN_KEYS[seat], token) === null) signOut(seat);
      }
    }, IDLE_TICK_MS);

    return () => {
      for (const name of events) window.removeEventListener(name, keepAwake);
      window.clearInterval(tick);
    };
  }, [signedIn, matchOpen, keepAwake, signOut]);

  return { seats, ready, signIn, signOut, update };
}
